import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { useGetDossierActionLogsQuery } from "actions/dossiers/queries";
import HeaderFirst from "utils/HeaderFirst";
import CustomBootstrapTable from "utils/reactTable";
import TablePaginators from "components/Pagination/TablePaginator";
import searchIcon from "../../image/search.png";

const formatDate = (dateString) => {
  if (!dateString) return "-";
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    }).format(new Date(dateString));
  } catch (e) {
    return dateString;
  }
};

export default function JournalPage() {
  const { clientID: dossier_id } = useParams();
  const { data: logsResponse, isLoading, error } = useGetDossierActionLogsQuery({ dossier_id });
  const [searchTerm, setSearchTerm] = useState("");
  const [actionFilter, setActionFilter] = useState("ALL");
  const [pageSize, setPageSize] = useState(20);
  const [paginationIndex, setPaginationIndex] = useState(0);

  const logs = logsResponse?.data || [];

  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      const matchSearch =
        log.libelle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.user?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.user?.last_name?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchFilter = actionFilter === "ALL" || log.type_action === actionFilter;

      return matchSearch && matchFilter;
    });
  }, [logs, searchTerm, actionFilter]);

  const currentPageData = useMemo(() => {
    return filteredLogs.slice(paginationIndex * pageSize, (paginationIndex + 1) * pageSize);
  }, [filteredLogs, paginationIndex, pageSize]);

  const handlePageChange = (newIndex) => {
    setPaginationIndex(newIndex);
  };

  const getActionBadgeLabel = (type) => {
    switch (type) {
      case "CREATE":
        return "Création";
      case "UPDATE":
        return "Modification";
      case "DELETE":
        return "Suppression";
      case "IMPORT":
        return "Import Excel";
      default:
        return type;
    }
  };

  const columns = [
    {
      dataField: "created_at",
      text: "Date & Heure",
      formatter: (cell) => formatDate(cell),
    },
    {
      dataField: "user",
      text: "Utilisateur",
      formatter: (cell) => {
        const userFullName = cell
          ? `${cell.first_name} ${cell.last_name}`
          : "Système";
        const userEmail = cell?.email || "";
        return (
          <div>
            <span className="fw-semibold text-dark">{userFullName}</span>
            {userEmail && (
              <div className="text-muted" style={{ fontSize: "11px" }}>
                {userEmail}
              </div>
            )}
          </div>
        );
      },
    },
    {
      dataField: "type_action",
      text: "Type d'action",
      formatter: (cell) => (
        <span 
          className="px-2.5 py-1 rounded" 
          style={{ 
            backgroundColor: "#f3f4f6", 
            color: "#374151", 
            fontSize: "12px", 
            fontWeight: "600",
            display: "inline-block"
          }}
        >
          {getActionBadgeLabel(cell)}
        </span>
      ),
    },
    {
      dataField: "libelle",
      text: "Description",
      formatter: (cell) => <span className="text-dark fw-medium">{cell}</span>,
    },
  ];

  const nom = (
    <div style={{ display: 'flex' }}>
      <h4 className="titre-dossier">{"Journal d'activité"}</h4>
      <span className="nbrDossier">{filteredLogs?.length}</span>
    </div>
  );
  
  const description = <div>{"Historique des actions effectuées sur le dossier"}</div>;

  return (
    <div
      className="global"
      style={{
        backgroundColor: 'white',
        minHeight: '500px',
        borderRadius: '20px',
        padding: '20px',
        fontFamily: 'Montserrat'
      }}
    >
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 pb-3">
        <HeaderFirst nom={nom} description={description} />
      </div>

      <hr style={{ margin: "15px 0 0 0" }} />
      <div className="d-flex align-items-center justify-content-between py-2 flex-wrap gap-3">
        {/* Filtre Dropdown */}
        <div className="d-flex align-items-center gap-2">
          <span style={{ fontSize: "13px", fontWeight: "600", color: "#6b7280" }}>{"Filtrer :"}</span>
          <select
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value);
              setPaginationIndex(0);
            }}
            className="form-select border-0 bg-light rounded-3 px-3 py-1.5"
            style={{
              fontWeight: "600",
              fontSize: "13px",
              outline: "none",
              cursor: "pointer",
              minWidth: "160px"
            }}
          >
            <option value="ALL">Toutes les actions</option>
            <option value="CREATE">Créations</option>
            <option value="UPDATE">Modifications</option>
            <option value="DELETE">Suppressions</option>
            <option value="IMPORT">Imports Excel</option>
          </select>
        </div>

        {/* Barre de recherche */}
        <div className="d-flex align-items-center">
          <img src={searchIcon} alt="Rechercher" style={{ width: "16px", height: "16px" }} />
          <input
            type="text"
            className="lineSeearch ps-2 py-2"
            placeholder="Rechercher..."
            style={{
              fontWeight: "600",
              fontSize: "13px",
              border: "none",
              outline: "none",
            }}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPaginationIndex(0);
            }}
          />
        </div>
      </div>
      <hr style={{ margin: "0 0 20px 0" }} />

      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <Spinner animation="border" style={{ color: '#009688' }} role="status" className="me-2" />
          <span className="text-muted">{"Chargement de l'historique..."}</span>
        </div>
      ) : error ? (
        <div className="text-center py-5">
          <p className="text-danger mb-0">{"Une erreur est survenue lors de la récupération du journal d'activité."}</p>
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <p className="mb-0">{"Aucune action enregistrée ne correspond à vos critères."}</p>
        </div>
      ) : (
        <>
          <div className="table-responsive-sm">
            <CustomBootstrapTable
              keyField="id"
              data={currentPageData}
              columns={columns}
            />
          </div>

          {filteredLogs.length > 0 && (
            <div className="mt-4">
              <TablePaginators
                paginationIndex={paginationIndex}
                setPaginationIndex={handlePageChange}
                dataLength={filteredLogs.length}
                pageSize={pageSize}
                setPageSize={setPageSize}
                buttonHide="d-none"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
